import React, { useCallback, useEffect, useState } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import {
  Box,
  Typography,
  Paper,
  TextField,
  Button,
  List,
  ListItem,
  ListItemText,
  IconButton,
  Divider,
  Chip,
  CircularProgress,
  Alert,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import SaveIcon from '@mui/icons-material/Save';
import CancelIcon from '@mui/icons-material/Cancel';
import NoteAddIcon from '@mui/icons-material/NoteAdd';
import DescriptionIcon from '@mui/icons-material/Description';
import { formatDateTimeInAppTimeZone } from '../utils/dateTime';

// Empty string for same-origin API calls (nginx proxies /api/)
const API_BASE_URL = process.env.REACT_APP_API_BASE_URL ?? '';

const TickerNotes = ({ ticker }) => {
  const { user, isAuthenticated, getAccessTokenSilently } = useAuth0();
  const userEmail = isAuthenticated && user?.email ? String(user.email).trim().toLowerCase() : '';
  const userName = isAuthenticated && user?.name ? String(user.name).trim() : '';
  const userId = isAuthenticated && user?.sub ? user.sub : 'anonymous';
  const [authToken, setAuthToken] = useState('');

  useEffect(() => {
    let active = true;

    const loadToken = async () => {
      if (!isAuthenticated) {
        if (active) setAuthToken('');
        return;
      }

      try {
        const token = await getAccessTokenSilently();
        if (active) {
          setAuthToken(String(token || ''));
        }
      } catch (error) {
        if (active) {
          setAuthToken('');
        }
      }
    };

    loadToken();

    return () => {
      active = false;
    };
  }, [isAuthenticated, getAccessTokenSilently]);

  const buildAuthHeaders = useCallback(async ({ preferFresh = false } = {}) => {
    const headers = {
      'X-User-Id': userId,
      ...(userEmail ? { 'X-User-Email': userEmail } : {}),
      ...(userName ? { 'X-User-Name': userName } : {}),
    };

    if (!isAuthenticated) {
      return headers;
    }

    let token = authToken;
    if (!token || preferFresh) {
      try {
        token = String((await getAccessTokenSilently({ cacheMode: preferFresh ? 'off' : undefined })) || '');
        if (token) {
          setAuthToken(token);
        }
      } catch (error) {
        token = '';
      }
    }

    if (token) {
      headers.Authorization = `Bearer ${token}`;
    }

    return headers;
  }, [authToken, getAccessTokenSilently, isAuthenticated, userEmail, userId, userName]);

  const fetchWithAuthRetry = useCallback(async (url, init = {}) => {
    const runRequest = async (preferFresh) => {
      const requestHeaders = await buildAuthHeaders({ preferFresh });
      return fetch(url, {
        ...init,
        headers: {
          ...requestHeaders,
          ...(init.headers || {}),
        },
      });
    };

    let response = await runRequest(false);
    if (response.status === 401 && isAuthenticated) {
      setAuthToken('');
      response = await runRequest(true);
    }

    return response;
  }, [buildAuthHeaders, isAuthenticated]);
  
  const [notes,      setNotes]      = useState([]);
  const [loading,    setLoading]    = useState(false);
  const [error,      setError]      = useState(null);
  const [newContent, setNewContent] = useState('');
  const [saving,     setSaving]     = useState(false);
  const [editingId,  setEditingId]  = useState(null);
  const [editContent, setEditContent] = useState('');

  useEffect(() => {
    if (!ticker) return;
    setLoading(true);
    setError(null);
    (async () => {
      try {
        const response = await fetchWithAuthRetry(`${API_BASE_URL}/api/notes/${encodeURIComponent(ticker)}`);
        if (!response.ok) throw new Error(`HTTP ${response.status}`);
        const data = await response.json();
        setNotes(Array.isArray(data) ? data : []);
      } catch (err) {
        setError('Failed to load notes: ' + err.message);
      } finally {
        setLoading(false);
      }
    })();
  }, [fetchWithAuthRetry, ticker]);

  const handleAdd = async () => {
    if (!newContent.trim()) return;
    setSaving(true);
    try {
      const res = await fetchWithAuthRetry(
        `${API_BASE_URL}/api/notes/${encodeURIComponent(ticker)}`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', ...(await buildAuthHeaders()) },
          body: JSON.stringify({ content: newContent.trim() }),
        }
      );
      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.error || 'Save failed');
      }
      const note = await res.json();
      setNotes((prev) => [note, ...prev]);
      setNewContent('');
    } catch (err) {
      setError('Failed to save note: ' + err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleUpdate = async (id) => {
    if (!editContent.trim()) return;
    try {
      const res = await fetchWithAuthRetry(`${API_BASE_URL}/api/notes/${id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', ...(await buildAuthHeaders()) },
        body: JSON.stringify({ content: editContent.trim() }),
      });
      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.error || 'Update failed');
      }
      const updated = await res.json();
      setNotes((prev) => prev.map((n) => (n.id === id ? updated : n)));
      setEditingId(null);
    } catch (err) {
      setError('Failed to update note: ' + err.message);
    }
  };

  const handleDelete = async (id) => {
    try {
      const res = await fetchWithAuthRetry(`${API_BASE_URL}/api/notes/${id}`, {
        method: 'DELETE',
        headers: await buildAuthHeaders(),
      });
      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.error || 'Delete failed');
      }
      setNotes((prev) => prev.filter((n) => n.id !== id));
    } catch (err) {
      setError('Failed to delete note: ' + err.message);
    }
  };

  const startEdit = (note) => {
    setEditingId(note.id);
    setEditContent(note.content);
  };

  if (!ticker) return null;

  return (
    <Box>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
        <DescriptionIcon color="primary" />
        <Typography variant="h5" fontWeight={700}>
          Investment Thesis
        </Typography>
        <Chip label={ticker} color="primary" size="small" sx={{ ml: 0.5 }} />
        <Chip
          label={`${notes.length} note${notes.length !== 1 ? 's' : ''}`}
          variant="outlined"
          size="small"
        />
      </Box>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Record your investment thesis, key catalysts, risks, and observations for{' '}
        <strong>{ticker}</strong>.
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* New note input */}
      <Paper variant="outlined" sx={{ p: 2, mb: 3 }}>
        <Typography variant="subtitle2" fontWeight={600} gutterBottom>
          Add Note
        </Typography>
        <TextField
          multiline
          rows={4}
          fullWidth
          placeholder={`Write your analysis, thesis, or key observations for ${ticker}…`}
          value={newContent}
          onChange={(e) => setNewContent(e.target.value)}
          inputProps={{ maxLength: 10000 }}
          sx={{ mb: 1 }}
        />
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="caption" color="text.secondary">
            {newContent.length} / 10 000
          </Typography>
          <Button
            variant="contained"
            startIcon={saving ? <CircularProgress size={16} /> : <NoteAddIcon />}
            onClick={handleAdd}
            disabled={!newContent.trim() || saving}
          >
            Save Note
          </Button>
        </Box>
      </Paper>

      {/* Notes list */}
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
          <CircularProgress />
        </Box>
      ) : notes.length === 0 ? (
        <Paper
          variant="outlined"
          sx={{ p: 4, textAlign: 'center', bgcolor: 'background.default' }}
        >
          <DescriptionIcon sx={{ fontSize: 48, color: 'text.disabled', mb: 1 }} />
          <Typography color="text.secondary">
            No notes yet. Add your first observation above.
          </Typography>
        </Paper>
      ) : (
        <List disablePadding>
          {notes.map((note, idx) => (
            <React.Fragment key={note.id}>
              {idx > 0 && <Divider />}
              <ListItem
                alignItems="flex-start"
                sx={{ px: 0, py: 1.5 }}
                secondaryAction={
                  editingId !== note.id ? (
                    <Box>
                      <IconButton size="small" onClick={() => startEdit(note)}>
                        <EditIcon fontSize="small" />
                      </IconButton>
                      <IconButton
                        size="small"
                        onClick={() => handleDelete(note.id)}
                        color="error"
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Box>
                  ) : null
                }
              >
                <ListItemText
                  primary={
                    editingId === note.id ? (
                      <Box>
                        <TextField
                          multiline
                          fullWidth
                          value={editContent}
                          onChange={(e) => setEditContent(e.target.value)}
                          inputProps={{ maxLength: 10000 }}
                          size="small"
                          sx={{ mb: 1 }}
                        />
                        <Box sx={{ display: 'flex', gap: 1 }}>
                          <Button
                            size="small"
                            variant="contained"
                            startIcon={<SaveIcon />}
                            onClick={() => handleUpdate(note.id)}
                            disabled={!editContent.trim()}
                          >
                            Save
                          </Button>
                          <Button
                            size="small"
                            startIcon={<CancelIcon />}
                            onClick={() => setEditingId(null)}
                          >
                            Cancel
                          </Button>
                        </Box>
                      </Box>
                    ) : (
                      <Typography
                        variant="body2"
                        sx={{ whiteSpace: 'pre-wrap', pr: 8 }}
                      >
                        {note.content}
                      </Typography>
                    )
                  }
                  secondary={
                    <Typography variant="caption" color="text.secondary">
                      {formatDateTimeInAppTimeZone(note.created_at)}
                      {note.updated_at !== note.created_at ? ' · edited' : ''}
                    </Typography>
                  }
                />
              </ListItem>
            </React.Fragment>
          ))}
        </List>
      )}
    </Box>
  );
};

export default TickerNotes;
